<template>
  <div class="level-page">
    <div class="page-header">
      <h2>编辑指标详情</h2>
    </div>

    <div class="page-content">
      <div class="format-info">{{ levelData.numId }}{{ levelData.name }}</div>

      <el-descriptions direction="vertical" :column="1" border>
        <el-descriptions-item>
          <template #label>
            <span style="color: #f56c6c">*</span> 指标名称
          </template>
          <el-input
            v-model="formData.name"
            placeholder="指标名称"
            :class="{ 'is-invalid': nameInvalid }"
          />
          <div
            v-if="nameInvalid"
            style="color: #f56c6c; font-size: 12px; margin-top: 2px"
          >
            指标名称不能为空
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="指标详细内容">
          <el-input
            v-model="formData.content"
            type="textarea"
            rows="4"
            placeholder="指标组一内容"
          />
        </el-descriptions-item>

        <el-descriptions-item label="图片">
          <MultipleUploadComponent
            ref="multiUploadRef"
            v-model="formData.imageList"
            @change="handleImagesChange"
          />
        </el-descriptions-item>
      </el-descriptions>

      <div class="action-buttons">
        <el-button type="primary" @click="handleSubmit">保存</el-button>
        <el-button @click="handleCancel">取消</el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import MultipleUploadComponent from "@/components/MultipleUploadComponent.vue";
import {
  addLevel,
  updateLevel,
  insertLevel,
  searchLevelDetail,
} from "@/api/level";
import { ElMessage } from "element-plus";

interface LevelForm {
  id: string;
  standardId: string;
  numId: string;
  level: number;
  name: string;
  content: string;
  imageList?: string[];
  ifNextLevel?: number;
  image?: string;
  insertBefore?: boolean;
  relativeNumId?: string;
}

const props = defineProps<{
  levelData: LevelForm;
  isNew?: boolean;
  insertBefore?: boolean; // 新增插入标记
  relativeNumId?: string; // 新增关联编号
}>();
const nameInvalid = ref(false);

const formData = ref<LevelForm>({
  id: props.levelData.id,
  standardId: props.levelData.standardId,
  numId: props.levelData.numId,
  level: props.levelData.level,
  name: props.levelData.name,
  content: props.levelData.content || "",
  imageList: props.levelData.imageList || [],
  ifNextLevel: props.levelData.ifNextLevel,
});

const emit = defineEmits<{
  update: [data: Partial<LevelForm>];
}>();

// 添加处理多图上传变化的方法
const handleImagesChange = (urls: string[]) => {
  formData.value.imageList = urls;
  // 保持单图兼容性
  formData.value.image = urls.length > 0 ? urls[0] : "";
};
const handleSubmit = async () => {
  try {
    // 校验必填
    nameInvalid.value = !formData.value.name;
    if (nameInvalid.value) {
      ElMessage.warning("指标名称不能为空");
      return;
    }

    const requestData = {
      content: formData.value.content,
      ifNextLevel: formData.value.ifNextLevel || 0,
      imageList: formData.value.imageList,
      level: formData.value.level,
      name: formData.value.name,
      numId: formData.value.numId,
      standardId: Number(formData.value.standardId),
    };
    let response;
    let finalId: string | null = null;
    // 处理插入逻辑
    if (props.insertBefore) {
      const insertPayload = {
        ...requestData,
        relativeNumId: props.levelData.numId,
      };
      // 第一步：执行插入操作
      response = await insertLevel(insertPayload);

      // 第二步：获取新ID
      const searchResponse = await searchLevelDetail({
        level: requestData.level.toString(),
        numId: props.levelData.numId,
        standardId: requestData.standardId.toString(),
      });

      if (searchResponse?.code === 1 && searchResponse.data?.id) {
        finalId = searchResponse.data.id.toString();
      }
    } // 处理新增逻辑
    else if (props.isNew) {
      response = await addLevel(requestData);
      finalId = response.data?.id;
    }
    // 处理更新逻辑
    else {
      response = await updateLevel({
        ...requestData,
        id: Number(formData.value.id),
      });
    }
    if (response?.code === 1) {
      ElMessage.success(
        props.insertBefore ? "插入成功" : props.isNew ? "新增成功" : "更新成功"
      );

      // 触发全局更新
      emit("update", {
        ...formData.value,
        id: finalId || formData.value.id,
        needRefresh: true,
        isNew: false,
        insertBefore: false,
      });
    }
  } catch (error) {
    ElMessage.error("保存失败");
  }
};

const handleCancel = () => {
  console.log("取消操作");
};

// 添加 uploadComponentRef 的引用
const uploadComponentRef = ref();

watch(
  () => props.levelData,
  (newVal) => {
    if (newVal) {
      formData.value = {
        id: newVal.id,
        standardId: newVal.standardId,
        numId: newVal.numId,
        level: newVal.level,
        name: newVal.name,
        content: newVal.content || "",
        imageList: newVal.image ? newVal.image.split(",") : [],
        ifNextLevel: newVal.ifNextLevel,
        // 新增字段
        insertBefore: newVal.insertBefore,
        relativeNumId: newVal.relativeNumId,
      };
      uploadComponentRef.value?.clearFiles();
    }
  },
  { immediate: true, deep: true }
);
</script>

<style scoped>
.standard-page {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.page-header h3 {
  margin: 10px 0;
  font-size: 16px;
  color: #666;
}

.id-info {
  background-color: #f0f9eb;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 4px;
}

.format-info {
  margin-bottom: 20px;
  color: #666;
}

.page-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
}

.action-buttons {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 20px;
}

.image-preview {
  position: relative;
  display: inline-block;
  margin-top: 10px;
}

.delete-image-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  padding: 4px;
  opacity: 0; /* 默认隐藏 */
  transition: opacity 0.3s; /* 添加过渡效果 */
}

/* 鼠标悬停时显示删除按钮 */
.image-preview-item:hover .delete-image-btn {
  opacity: 1;
}

:deep(.el-descriptions) {
  margin: 20px 0;
}

:deep(.el-descriptions__cell) {
  padding: 20px;
}

.image-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.image-preview-item {
  position: relative;
  width: 100px;
  height: 100px;
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 14px;
}
/* 添加高亮样式 */
.el-input__inner.is-invalid {
  border-color: #f56c6c !important; /* 红色边框 */
  background-color: #fff5f5; /* 浅红背景 */
}
</style>
